import React, {FC, useCallback, useEffect, useState} from 'react'
import {useHistory} from 'react-router-dom'
import {ToastContainer} from 'react-toastify'
import {PageTitle} from '../../../../_metronic/layout/core'
import {ConfirmModal} from '../../../../shared/components/ConfirmModal'
import DataTable, {TableAction, TableColumn} from '../../../../shared/components/DataTable'
import {showErrorToast, showSuccessToast} from '../../../../shared/components/ToastUtils'
import {apiErrorMessage} from '../../public/PublicModels'
import {fullName} from '../application/ApplicationModel'
import {formatDateTime} from '../shared/DocumentApi'
import {emptyFilters, IListFilters, SearchFilterBar} from '../shared/SearchFilterBar'
import {useJobProcessOptions} from '../shared/useJobProcessOptions'
import {deleteCandidate, searchCandidates, syncApplicationsToPool, updateCandidate} from './Api'
import {candidateDetailPath, ICandidateModel, ICandidateUpdateRequest} from './CandidateModel'
import {CandidateEditModal} from './CandidateEditModal'
import {AssignCallPoolModal, IAssignTarget} from '../call-pool/AssignCallPoolModal'
import {SelectCheckbox, SelectionToolbar, useSelection} from '../shared/Selection'
import {ITagTarget, removeCandidateTag, TagAssignModal} from '../tag/TagAssignModal'
import {useTagOptions} from '../tag/useTagOptions'
import {ITagModel} from '../tag/TagModel'
import {TagChips} from '../tag/TagChips'

/** Aday havuzu: arama, ilana göre filtre, künye, düzenleme ve silme */
const CandidatePage: FC = () => {
  const history = useHistory()
  const {jobs} = useJobProcessOptions()
  const [filters, setFilters] = useState<IListFilters>(emptyFilters)
  const [candidates, setCandidates] = useState<ICandidateModel[]>([])
  const [currentPage, setCurrentPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [syncing, setSyncing] = useState(false)
  const [editingItem, setEditingItem] = useState<ICandidateModel | null>(null)
  const [deletingItem, setDeletingItem] = useState<ICandidateModel | null>(null)
  const [tagTarget, setTagTarget] = useState<ITagTarget | null>(null)
  const [assignTarget, setAssignTarget] = useState<IAssignTarget | null>(null)
  const selection = useSelection()
  const {tags, reloadTags} = useTagOptions()

  const fetchCandidates = useCallback(async (page: number, activeFilters: IListFilters) => {
    setLoading(true)
    try {
      const payload = (await searchCandidates(activeFilters, page)).data.payload
      setCandidates(payload.content)
      setTotalPages(payload.totalPages)
      setCurrentPage(page)
    } catch (error) {
      showErrorToast(apiErrorMessage(error, 'Adaylar yüklenemedi'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchCandidates(0, filters)
  }, [filters, fetchCandidates])

  const handleSearch = useCallback((q: string) => setFilters((current) => ({...current, q})), [])
  const handleFilter = useCallback(
    (selection: Omit<IListFilters, 'q'>) => setFilters((current) => ({...current, ...selection})),
    []
  )

  const handleSync = async () => {
    setSyncing(true)
    try {
      const {data} = await syncApplicationsToPool()
      showSuccessToast(data.payload > 0 ? `${data.payload} başvuru havuza aktarıldı` : 'Aktarılacak başvuru yok')
      await fetchCandidates(0, filters)
    } catch (error) {
      showErrorToast(apiErrorMessage(error, 'Başvurular aktarılamadı'))
    } finally {
      setSyncing(false)
    }
  }

  const toTagTarget = (candidate: ICandidateModel): ITagTarget => ({
    candidateId: candidate.id,
    candidateName: fullName(candidate),
    tags: candidate.tags || [],
  })

  const handleRemoveTag = async (candidate: ICandidateModel, tag: ITagModel) => {
    try {
      await removeCandidateTag(toTagTarget(candidate), tag)
      await fetchCandidates(currentPage, filters)
    } catch (error) {
      showErrorToast(apiErrorMessage(error, 'Etiket kaldırılamadı'))
    }
  }

  const handleUpdate = async (request: ICandidateUpdateRequest) => {
    if (!editingItem) return
    setSaving(true)
    try {
      await updateCandidate(editingItem.id, request)
      showSuccessToast('Aday güncellendi')
      setEditingItem(null)
      await fetchCandidates(currentPage, filters)
    } catch (error) {
      showErrorToast(apiErrorMessage(error, 'Aday güncellenemedi'))
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!deletingItem) return
    setLoading(true)
    try {
      await deleteCandidate(deletingItem.id)
      showSuccessToast('Aday havuzdan silindi')
      await fetchCandidates(currentPage, filters)
    } catch (error) {
      showErrorToast(apiErrorMessage(error, 'Aday silinemedi'))
      setLoading(false)
    } finally {
      setDeletingItem(null)
    }
  }

  const columns: TableColumn<ICandidateModel>[] = [
    {
      key: 'select',
      label: '',
      width: '40px',
      render: (_, candidate) => (
        <SelectCheckbox
          checked={selection.selectedIds.includes(candidate.id)}
          onChange={() => selection.toggle(candidate.id)}
        />
      ),
    },
    {
      key: 'firstname',
      label: 'Aday',
      render: (_, candidate) => (
        <div className='d-flex flex-column'>
          <span className='fw-bolder text-gray-800'>{fullName(candidate)}</span>
          {candidate.candidateLocation && <span className='text-muted fs-7'>{candidate.candidateLocation}</span>}
          <TagChips tags={candidate.tags} onRemove={(tag) => handleRemoveTag(candidate, tag)} />
        </div>
      ),
    },
    {
      key: 'email',
      label: 'İletişim',
      render: (_, candidate) => (
        <div className='d-flex flex-column fs-7'>
          <span>{candidate.email || '-'}</span>
          <span className='text-muted'>{candidate.phone}</span>
        </div>
      ),
    },
    {
      key: 'applicationCount',
      label: 'Başvuru',
      width: '90px',
      render: (count: number) => <span className='badge badge-light-primary'>{count}</span>,
    },
    {
      key: 'lastApplicationDate',
      label: 'Son Başvuru',
      width: '150px',
      render: (value) => <span className='fs-7'>{formatDateTime(value)}</span>,
    },
  ]

  const actions: TableAction<ICandidateModel>[] = [
    {
      label: 'Künye',
      icon: 'bi bi-person-vcard',
      className: 'btn-light-info me-2',
      onClick: (candidate) => history.push(candidateDetailPath(candidate.id)),
    },
    {
      label: 'Arama Havuzuna Aktar',
      icon: 'bi bi-telephone-plus',
      className: 'btn-light-success me-2',
      onClick: (candidate) => setAssignTarget({candidateIds: [candidate.id], label: fullName(candidate)}),
    },
    {
      label: 'Etiketle',
      icon: 'bi bi-tags',
      className: 'btn-light-warning me-2',
      onClick: (candidate) => setTagTarget(toTagTarget(candidate)),
    },
    {label: 'Düzenle', icon: 'bi bi-pencil', className: 'btn-light-primary me-2', onClick: setEditingItem},
    {label: 'Sil', icon: 'bi bi-trash', className: 'btn-light-danger', onClick: setDeletingItem},
  ]

  return (
    <>
      <PageTitle breadcrumbs={[]}>Aday Havuzu</PageTitle>

      <div className='card'>
        <div className='card-header border-0 pt-5'>
          <h3 className='card-title align-items-start flex-column'>
            <span className='card-label fw-bolder fs-3 mb-1'>Aday Havuzu</span>
            <span className='text-muted mt-1 fw-bold fs-7'>
              Başvuran tüm adaylar; e-posta veya telefonu eşleşen yeni başvuru mevcut adayın CV'sini günceller
            </span>
          </h3>
          <div className='card-toolbar'>
            <button type='button' className='btn btn-sm btn-light-primary' onClick={handleSync} disabled={syncing}>
              {syncing ? (
                <span className='spinner-border spinner-border-sm' />
              ) : (
                <>
                  <i className='bi bi-arrow-repeat'></i>
                  Eski Başvuruları Aktar
                </>
              )}
            </button>
          </div>
        </div>
        <div className='card-body pt-0'>
          <SearchFilterBar jobs={jobs} tags={tags} onSearch={handleSearch} onFilter={handleFilter} />
          <SelectionToolbar
            pageIds={candidates.map((candidate) => candidate.id)}
            selectedIds={selection.selectedIds}
            onTogglePage={selection.togglePage}
            onClear={selection.clear}
            onTransfer={() =>
              setAssignTarget({candidateIds: selection.selectedIds, label: `${selection.selectedIds.length} aday`})
            }
          />
          <DataTable
            data={candidates}
            columns={columns}
            actions={actions}
            showPagination={true}
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={(page) => page >= 0 && page < totalPages && fetchCandidates(page, filters)}
            loading={loading}
            emptyMessage='Havuzda aday bulunamadı'
          />
        </div>
      </div>

      <CandidateEditModal
        candidate={editingItem}
        loading={saving}
        onClose={() => setEditingItem(null)}
        onSubmit={handleUpdate}
      />

      <AssignCallPoolModal
        target={assignTarget}
        onClose={() => setAssignTarget(null)}
        onAssigned={selection.clear}
      />

      <TagAssignModal
        target={tagTarget}
        onClose={() => setTagTarget(null)}
        onSaved={() => {
          reloadTags()
          fetchCandidates(currentPage, filters)
        }}
      />

      <ConfirmModal
        show={!!deletingItem}
        header='Silme Onayı'
        text={`${deletingItem ? fullName(deletingItem) : ''} havuzdan silinecek. Başvuruları silinmez, yalnızca adayla bağlantıları kaldırılır. Devam edilsin mi?`}
        handleClose={() => setDeletingItem(null)}
        applyOnClick={handleDelete}
      />

      <ToastContainer position='top-right' autoClose={3000} hideProgressBar={false} newestOnTop closeOnClick pauseOnHover />
    </>
  )
}

export {CandidatePage}
